import React from "react";
import { commitMutation, graphql } from "react-relay";
import { useFragment } from "react-relay";

import { faCopy, faPen, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faAdd, faEllipsisV, faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faExternalLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import copy from "copy-to-clipboard";

import { ReactComponent as FellowLogo } from "~/legacy-assets/images/logos/fellow-icon-black.svg";
import { useBookmarkModal } from "~/meetings/SeriesSidePanel/Bookmarks/BookmarkModal";
import { EmptyState } from "~/meetings/SeriesSidePanel/EmptyState";
import { SidePanelItem } from "~/meetings/SeriesSidePanel/SidePanelItem";
import { environment } from "~/models";
import IconButton from "~/ui/button/IconButton";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";

import type { Bookmarks_stream$data, Bookmarks_stream$key } from "~/__generated__/Bookmarks_stream.graphql";
import type { Bookmarks_viewer$data, Bookmarks_viewer$key } from "~/__generated__/Bookmarks_viewer.graphql";
import type { BookmarksRemoveBookmarkMutation } from "~/__generated__/BookmarksRemoveBookmarkMutation.graphql";

import styles from "./Bookmarks.module.css";

const bookmarksStreamFragment = graphql`
	fragment Bookmarks_stream on NoteStream {
		id
		tabs {
			id
			otherStreamId
			icon
			title
			order
			isEmbedded
			url
		}
	}
`;

const bookmarksViewerFragment = graphql`
	fragment Bookmarks_viewer on Viewer {
		account {
			uri
		}
	}
`;

export type Bookmark = Bookmarks_stream$data["tabs"][0];

const removeBookmarkMutation = graphql`
	mutation BookmarksRemoveBookmarkMutation($streamId: ID!, $tabId: String!) {
		StreamTab_RemoveTab(streamId: $streamId, tabId: $tabId) {
			stream {
				id
				...Bookmarks_stream @relay(mask: false)
			}
		}
	}
`;

const Bookmark = ({
	streamId,
	bookmark,
	account,
}: {
	streamId: string;
	bookmark: Bookmarks_stream$data["tabs"][0];
	account: Bookmarks_viewer$data["account"];
}) => {
	const openEditBookmarkModal = useBookmarkModal(streamId, bookmark);

	const url = React.useMemo(() => {
		if (bookmark.otherStreamId) {
			return `${account.uri}/stream/${bookmark.otherStreamId}`;
		} else if (bookmark.url) {
			return bookmark.url;
		} else {
			Sentry.captureException(new Error("Bookmark has no URL"));
			return "";
		}
	}, [bookmark.otherStreamId, bookmark.url, account?.uri]);

	const copyLink = React.useCallback(() => {
		copy(url);
		toast.info(`Copied to clipboard!`, { autoClose: 5000 });
	}, [url]);

	const removeBookmark = React.useCallback(() => {
		commitMutation<BookmarksRemoveBookmarkMutation>(environment, {
			mutation: removeBookmarkMutation,
			variables: {
				streamId: streamId,
				tabId: bookmark.id,
			},
			onCompleted: (response, errors) => {
				if (errors) {
					toast.error("We had a problem removing that tab, try again");
					throw new Error((errors || []).map(e => e.message).join(";"));
				}
			},
		});
	}, [streamId, bookmark.id]);

	const menuItems = React.useMemo(() => {
		const items: JSX.Element[] = [];
		if (bookmark.url) {
			items.push(
				<Menu.Item key="edit-bookmark" icon={faPen} onSelect={openEditBookmarkModal}>
					Edit bookmark
				</Menu.Item>,
			);
		}

		items.push(
			<Menu.Item key="copy-link" icon={faCopy} onSelect={copyLink}>
				Copy link
			</Menu.Item>,
		);

		if (items.length > 1) {
			items.push(<Menu.Separator key="delete-separator" />);
		}
		items.push(
			<Menu.Item key="delete" icon={faTrashCan} destructive onSelect={removeBookmark}>
				Delete
			</Menu.Item>,
		);
		return items;
	}, [bookmark.url, openEditBookmarkModal, copyLink, removeBookmark]);

	const icon = React.useMemo(() => {
		if (bookmark.icon === "fellow" && bookmark.otherStreamId) return <FellowLogo className={styles.icon} />;
		if (bookmark.icon) return <img className={styles.icon} src={bookmark.icon} alt={bookmark.title} />;
		return <FontAwesomeIcon className={styles.icon} icon={faLink} />;
	}, [bookmark.icon, bookmark.otherStreamId, bookmark.title]);

	return (
		<div className={styles.bookmark}>
			<a className={styles.bookmarkLink} href={url} target="_blank" rel="noopener noreferrer">
				{icon}
				<div className={styles.titleWrapper}>
					<p className={styles.title} title={bookmark.title}>
						{bookmark.title}
					</p>
					<FontAwesomeIcon className={styles.externalLinkIcon} icon={faExternalLink} />
				</div>
				<p className={styles.url}>{url}</p>
			</a>
			<div className={styles.menu}>
				<Menu>
					<Menu.Trigger>
						<IconButton icon={faEllipsisV} title="Configure this bookmark" variant="tertiary" />
					</Menu.Trigger>
					<Menu.Content align="end">{menuItems}</Menu.Content>
				</Menu>
			</div>
		</div>
	);
};

export default function Bookmarks({
	stream: streamKey,
	viewer: viewerKey,
	onClose,
}: {
	stream: Bookmarks_stream$key;
	viewer: Bookmarks_viewer$key;
	onClose?: () => void;
}) {
	const stream = useFragment(bookmarksStreamFragment, streamKey);
	const { account } = useFragment(bookmarksViewerFragment, viewerKey);
	const openAddBookmarkModal = useBookmarkModal(stream.id);

	/*
	The bookmarks side panel functions a bit differently than the Tabs in the old UI
	- We are only showing tabs that have a URL (with one caveat)
	- Links that were embedded within Fellow will now open in a new browser tab.
	- If a series had a link to another stream in Fellow, that will appear as a regular
		bookmark and open in another tab. However it can not be edited, only deleted.
		See the Bookmark component above for more details.
	*/

	const bookmarks = stream.tabs.filter(tab => tab.url || tab.otherStreamId);

	return (
		<SidePanelItem>
			<SidePanelItem.Header
				headerTitle={<SidePanelItem.Title>Bookmarks</SidePanelItem.Title>}
				createAction={openAddBookmarkModal}
				onClose={onClose}
			></SidePanelItem.Header>
			<SidePanelItem.Body>
				{bookmarks.length > 0 ? (
					<div className={styles.bookmarks}>
						{bookmarks.map(bookmark => (
							<Bookmark key={bookmark.id} streamId={stream.id} account={account} bookmark={bookmark} />
						))}
					</div>
				) : (
					<EmptyState
						title="No bookmarks yet"
						description="Save links to notes, meetings, or helpful sites, all in one place."
						buttonText="Add bookmark"
						buttonOnClick={openAddBookmarkModal}
						buttonIcon={faAdd}
					/>
				)}
			</SidePanelItem.Body>
		</SidePanelItem>
	);
}
